/**
 * LOS TRES MOMENTOS DE UNA OPERACION, para el formulario que los corrige.
 *
 * Existe porque el propietario se encontro con esto: «a veces la fecha se guarda
 * mal cuando ya esta cerrado el trade y no se puede hacer nada».
 *
 * Funciones puras y en su propio archivo para poder probarlas con `node:test` sin
 * montar nada (SPEC 20: cero dependencias de test en `web/`). Lo que se prueba
 * aqui no es la pantalla, son las dos conversiones que se hacen mal con mucha
 * facilidad —instante a `datetime-local` y de vuelta— y el orden, que es la unica
 * regla que la pantalla puede comprobar antes de molestar al servidor.
 *
 * LA ZONA ES LA DEL DISPOSITIVO, no la configurada en los ajustes. Es lo mismo
 * que hacen el relleno y el cierre con sus campos de hora, y tiene que seguir
 * siendo lo mismo: un `datetime-local` pinta y lee la hora del dispositivo y
 * punto. El dia bajo el que sale la publicacion lo decide el servidor con la zona
 * de los ajustes (SPEC 19.19), asi que la correccion llega como instante y la
 * fecha del listado no depende de esto.
 */

/** Un instante ISO del contrato, en lo que `datetime-local` sabe pintar. */
export function toLocalInput(instant: string | null | undefined): string {
  if (instant === null || instant === undefined || instant === "") return "";

  const date = new Date(instant);

  if (Number.isNaN(date.getTime())) return "";

  /* Se resta el desfase y se usa `toISOString`, que no es un rodeo: es la unica
     forma de tener los ceros delante sin escribirlos a mano para el ano, el mes,
     el dia, la hora y el minuto. */
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 16);
}

/** Lo que escribio el campo, como instante del contrato. Vacio es `null`. */
export function toInstant(local: string): string | null {
  if (local === "") return null;

  const date = new Date(local);

  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export type MomentsDraft = {
  planned: string;
  opened: string;
  closed: string;
};

/**
 * Lo que la pantalla puede decir SIN preguntar: que falta un momento que el
 * estado exige y que el orden no cuadra.
 *
 * Devuelve la frase o `null`. Son las mismas reglas que comprueba el agregado, y
 * eso es a proposito: aqui estan para no gastar un viaje y una respuesta de error
 * en algo que se ve en el formulario, no para sustituirlas. Si esta funcion
 * dijera `null` por error, el servidor lo rechazaria igual.
 */
export function momentsProblem(draft: MomentsDraft, needs: Needs): string | null {
  if (draft.planned === "") return "Falta el momento planificado.";
  if (needs.opened && draft.opened === "") return "Falta la hora de entrada.";
  if (needs.closed && draft.closed === "") return "Falta la hora de cierre.";

  const planned = Date.parse(draft.planned);
  const opened = draft.opened === "" ? null : Date.parse(draft.opened);
  const closed = draft.closed === "" ? null : Date.parse(draft.closed);

  if (opened !== null && opened < planned) {
    return "La entrada no puede ser anterior a la planificacion.";
  }

  if (closed !== null && opened !== null && closed < opened) {
    return "El cierre no puede ser anterior a la entrada.";
  }

  return null;
}

/**
 * Que momentos pide el estado de la operacion. Lo mismo que comprueba el
 * agregado: una abierta tiene entrada, una cerrada tiene las dos, y corregir no
 * cambia el estado.
 */
export type Needs = { opened: boolean; closed: boolean };

export function needsOf(status: string): Needs {
  return {
    opened: status === "open" || status === "closed",
    closed: status === "closed",
  };
}

/**
 * Nada que corregir: los tres campos dicen el mismo instante que ya esta
 * guardado.
 *
 * Se comparan como INSTANTES y no como texto: el campo pinta la hora del
 * dispositivo sin segundos, asi que un `2026-09-20T14:00:00.000Z` guardado y un
 * `2026-09-20T08:00` escrito son el mismo momento con dos cadenas distintas, y
 * compararlas en texto diria que hay cambios siempre.
 */
export function momentsChangeNothing(
  draft: MomentsDraft,
  stored: { plannedAtUtc: string; openedAtUtc: string | null; closedAtUtc: string | null },
): boolean {
  return (
    same(draft.planned, stored.plannedAtUtc)
    && same(draft.opened, stored.openedAtUtc)
    && same(draft.closed, stored.closedAtUtc)
  );
}

function same(local: string, stored: string | null): boolean {
  const written = toInstant(local);

  if (written === null || stored === null) return written === null && stored === null;

  /* Al minuto: el campo no tiene segundos, asi que un instante guardado con
     segundos nunca seria igual a uno escrito y el boton diria que hay cambios
     sin que nadie haya tocado nada. */
  return minute(written) === minute(stored);
}

function minute(instant: string): number {
  return Math.floor(Date.parse(instant) / 60_000);
}
